import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { hcApi } from './api';

const scripts = {};
const loadScript = (src) => {
  if (!scripts[src]) {
    scripts[src] = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src; el.async = true; el.onload = resolve; el.onerror = reject;
      document.head.appendChild(el);
    });
  }
  return scripts[src];
};
const loadYouTube = () => (window.YT?.Player ? Promise.resolve() : new Promise((resolve) => {
  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(); };
  loadScript('https://www.youtube.com/iframe_api');
}));
const loadVimeo = () => loadScript('https://player.vimeo.com/api/player.js');

let lastWarn = 0;
const warn = () => {
  if (Date.now() - lastWarn > 3000) { lastWarn = Date.now(); toast.info("Please watch the video without skipping ahead. We've taken you back to where you left off."); }
};

// Shared no-skip logic over a small player adapter: { time(), duration(), seek(t), setRate1(), playing() }
const startGuard = ({ adapter, initial, free, report, onDone }) => {
  let max = initial?.max_watched || 0;
  let done = !!initial?.completed;
  let lastReported = -1;
  let resumed = false;
  const send = (ended = false, force = false) => {
    if (!resumed || !adapter.duration()) return;
    const t = ended ? adapter.duration() : adapter.time();
    if (!ended && !force && Math.abs(t - lastReported) < 1) return;
    lastReported = t;
    report({ position: t, duration: adapter.duration() || null, ended }, t).then((r) => {
      if (r?.completed && !done) { done = true; onDone(); }
    }).catch(() => {});
  };
  const tick = () => {
    const t = adapter.time();
    if (!resumed && adapter.duration() > 0) {
      resumed = true;
      const pos = initial?.position || 0;
      if (t <= 1 && pos > 1 && pos < adapter.duration() - 3) adapter.seek(pos);
      return;
    }
    if (!done && !free && t > max + 2) { adapter.seek(max); warn(); return; }
    if (t > max) max = t;
  };
  const poll = setInterval(tick, 500);
  const beat = setInterval(() => { if (adapter.playing()) send(); }, 5000);
  return {
    tick, onEnded: () => { if (done || free || adapter.duration() - max <= 3) { max = adapter.duration(); send(true); } },
    onPause: () => send(false, true), onRate: () => { if (!done && !free) adapter.setRate1(); },
    stop: () => { clearInterval(poll); clearInterval(beat); send(false, true); },
  };
};

const html5Adapter = (v) => ({
  time: () => v.currentTime || 0, duration: () => (Number.isFinite(v.duration) ? v.duration : 0),
  seek: (t) => { v.currentTime = t; }, setRate1: () => { if (v.playbackRate > 1) v.playbackRate = 1; }, playing: () => !v.paused,
});

const attachHtml5 = (v, opts) => {
  const g = startGuard({ ...opts, adapter: html5Adapter(v) });
  const onSeeking = () => g.tick();
  v.addEventListener('seeking', onSeeking);
  v.addEventListener('ended', g.onEnded);
  v.addEventListener('pause', g.onPause);
  v.addEventListener('ratechange', g.onRate);
  return () => { v.removeEventListener('seeking', onSeeking); v.removeEventListener('ended', g.onEnded); v.removeEventListener('pause', g.onPause); v.removeEventListener('ratechange', g.onRate); g.stop(); };
};

const attachYouTube = async (iframe, opts) => {
  if (!/enablejsapi=1/.test(iframe.src)) iframe.src = `${iframe.src}${iframe.src.includes('?') ? '&' : '?'}enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
  await loadYouTube();
  let g = null;
  const player = new window.YT.Player(iframe, {
    events: {
      onReady: () => {
        g = startGuard({ ...opts, adapter: { time: () => player.getCurrentTime?.() || 0, duration: () => player.getDuration?.() || 0,
          seek: (t) => player.seekTo(t, true), setRate1: () => player.setPlaybackRate(1), playing: () => player.getPlayerState?.() === 1 } });
      },
      onStateChange: (e) => { if (!g) return; if (e.data === 0) g.onEnded(); if (e.data === 2) g.onPause(); },
      onPlaybackRateChange: (e) => { if (g && e.data > 1) g.onRate(); },
    },
  });
  return () => { if (g) g.stop(); };
};

const attachVimeo = async (iframe, opts) => {
  await loadVimeo();
  const player = new window.Vimeo.Player(iframe);
  const c = { t: 0, d: 0, playing: false };
  player.getDuration().then((d) => { c.d = d; });
  player.on('timeupdate', (e) => { c.t = e.seconds; c.d = e.duration; });
  const g = startGuard({ ...opts, adapter: { time: () => c.t, duration: () => c.d, seek: (t) => { c.t = t; player.setCurrentTime(t); },
    setRate1: () => player.setPlaybackRate(1).catch(() => {}), playing: () => c.playing } });
  player.on('play', () => { c.playing = true; });
  player.on('pause', () => { c.playing = false; g.onPause(); });
  player.on('ended', () => { c.playing = false; g.onEnded(); });
  player.on('seeked', (e) => { c.t = e.seconds; g.tick(); });
  player.on('playbackratechange', (e) => { if (e.playbackRate > 1) g.onRate(); });
  return () => g.stop();
};

const EMBED_SELECTOR = 'video, iframe[src*="youtube.com/embed"], iframe[src*="player.vimeo.com/video"]';

/** Attaches no-skip tracking to the main lesson video and every video embedded in the lesson text. */
export const useLessonVideoGuards = ({ courseId, lessonId, videos, free, mainRef, contentRef, onWatched }) => {
  const cb = useRef(onWatched);
  cb.current = onWatched;
  const lastPos = useRef({});
  useEffect(() => { lastPos.current = {}; }, [lessonId]);
  useEffect(() => {
    if (!videos?.required?.length) return undefined;
    const cleanups = [];
    let cancelled = false;
    const opts = (key) => ({
      initial: { ...videos.progress?.[key], position: lastPos.current[key] ?? videos.progress?.[key]?.position }, free,
      report: (body, t) => {
        lastPos.current[key] = t;
        return hcApi.post(`/courses/${courseId}/lessons/${lessonId}/video-progress`, { video_key: key, ...body }).then((r) => r.data);
      },
      onDone: () => cb.current(key),
    });
    if (mainRef.current && videos.required.includes('main')) cleanups.push(attachHtml5(mainRef.current, opts('main')));
    const embeds = contentRef.current ? Array.from(contentRef.current.querySelectorAll(EMBED_SELECTOR)) : [];
    embeds.forEach((el, i) => {
      const key = `e${i}`;
      if (!videos.required.includes(key)) return;
      if (el.tagName === 'VIDEO') { cleanups.push(attachHtml5(el, opts(key))); return; }
      const p = el.src.includes('vimeo') ? attachVimeo(el, opts(key)) : attachYouTube(el, opts(key));
      p.then((stop) => (cancelled ? stop() : cleanups.push(stop))).catch(() => {});
    });
    return () => { cancelled = true; cleanups.forEach((fn) => fn()); };
  }, [courseId, lessonId, videos, free, mainRef, contentRef]);
};

export const VideoLockHint = ({ remaining }) => (
  <p className="text-sm text-[#922A8C] mt-3 font-medium" data-testid="hc-video-lock-hint">
    {remaining === 1 ? 'Watch the video all the way to the end' : `Watch all ${remaining} videos all the way to the end`} to unlock Mark as Complete.
  </p>
);

export const MainLessonVideo = React.forwardRef(({ lesson, noRate }, ref) => (
  <video ref={ref} controls preload="metadata" className="hc-video mt-5" poster={lesson.video_poster || undefined} data-testid="hc-lesson-video"
    controlsList={noRate ? 'nodownload noplaybackrate' : undefined} disablePictureInPicture={noRate || undefined}>
    <source src={lesson.video_url} type="video/mp4" />
  </video>
));
